/* =====================================================================
   tower/driver/src/pay-feedback.js — Collect: show the driver WHAT a quick amount or a keypad tap changed.

   Parik 2026-10-22: tapping Exact / $40 / $50 "doesn't move anything — it looks as if nothing changes". So, only when
   a tap actually changed a figure (never on a sync tick or any other re-render):
     · the Received and Credit (overpaid → customer credit) figures roll from the old value to the new one (~300 ms) and their box pulses;
     · the Record button pulses and its text cross-fades when its text or enabled state changed;
     · a light haptic tap.
   Read before the render (paySnap) and compared after it (payFlash), by element id/data-act, so it works whether the
   screen was repainted wholesale or updated in place. prefers-reduced-motion: the final value at once and a static
   highlight (the colour, no motion). The selected quick amount itself is state, drawn by the screen (screens.js).
   ===================================================================== */
const ROOT = '#mod-driverapp .dh[data-dh-screen="collect"]';
const q = (s) => document.querySelector(ROOT + ' ' + s);
const rd = () => { const b = q('[data-act="record"]'); return { rec: txt('#dhReceived'), chg: txt('#dhCredit'), btn: b ? b.textContent.trim() + '|' + !!b.disabled : null }; };
const txt = (s) => { const e = q(s); return e ? e.textContent : null; };
// i18n-exempt: a media query, not words
const reduced = () => { try { return matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } };

/** What the Collect screen shows now (null when it is not on screen). */
export function paySnap() { return q('#dhReceived') ? Object.assign(rd(), { live: _live }) : null; }

let _seq = 0, _live = null;   // _live: the figures as they will END (a tap during a roll compares against those)
const timers = new Map();

/* The highlight on one element: restarted on every change, gone after ~0.9 s. */
function bump(el) {
  if (!el) return;
  el.classList.remove('dh-bump'); void el.offsetWidth; el.classList.add('dh-bump');
  clearTimeout(timers.get(el)); timers.set(el, setTimeout(() => { const now = el.isConnected ? el : null; if (now) now.classList.remove('dh-bump'); timers.delete(el); }, 900));
}
/* The number in a figure's text ("40.00", "$4.00", "4"), and the same text with another number in it. */
const NUM = /-?[\d,]*\.?\d+/;
const numOf = (s) => { const m = String(s || '').match(NUM); return m ? parseFloat(m[0].replace(/,/g, '')) : NaN; };
function withNum(final, v) {
  const m = String(final).match(NUM); if (!m) return final;
  const dec = (m[0].split('.')[1] || '').length, commas = m[0].includes(',');
  const n = commas ? v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }) : v.toFixed(dec);
  return final.replace(NUM, n);
}
/* Roll the figure `sel` from `from` to its current text over ~300 ms; the element is looked up every frame (a
   repaint mid-roll swaps it) and the last frame writes exactly the screen's own text. */
function roll(sel, from, seq) {
  const el0 = q(sel); if (!el0) return;
  const final = el0.textContent, a = numOf(from), b = numOf(final);
  if (!isFinite(a) || !isFinite(b) || a === b || reduced()) return;
  const t0 = performance.now(), D = 300;
  const step = (now) => {
    if (seq !== _seq) return;                     // a newer tap took over
    const el = q(sel); if (!el) return;
    const k = Math.min(1, (now - t0) / D), e = 1 - Math.pow(1 - k, 3);
    el.textContent = k < 1 ? withNum(final, a + (b - a) * e) : final;
    if (k < 1) requestAnimationFrame(step);
  };
  el0.textContent = withNum(final, a);
  requestAnimationFrame(step);
}

/** After a tap's render: signal every figure that changed against `before` (from paySnap). o.typed: a keypad digit —
    Received shows exactly what was typed (pulse, no roll); the figures derived from it still roll. */
export function payFlash(before, o, again) {
  o = o || {};
  if (!before) return;
  const after = rd(), was = before.live || before;
  // A render that paints on the next frame (not this one): look again then, once.
  if (!again && after.rec === was.rec && after.chg === was.chg && after.btn === was.btn) { requestAnimationFrame(() => payFlash(before, o, true)); return; }
  const seq = ++_seq;
  _live = after;
  let changed = false;
  if (after.rec !== was.rec && after.rec != null) { changed = true; const el = q('#dhReceived'); bump(el && el.parentElement);
    if (before.rec != null && o.typed !== true) roll('#dhReceived', before.rec, seq); }   // typed digits show as typed
  if (after.chg !== was.chg && after.chg != null) { changed = true; const el = q('#dhCredit'); bump(el && el.parentElement);
    if (before.chg != null) roll('#dhCredit', before.chg, seq); }
  if (after.btn !== was.btn && after.btn != null) { changed = true; bump(q('[data-act="record"]')); }
  if (changed) { try { if (navigator.vibrate) navigator.vibrate(10); } catch (e) { /* not on this phone */ } }
  setTimeout(() => { if (seq === _seq) _live = null; }, 400);
}
